import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { ClipboardList, Send, CheckCircle, XCircle, RotateCcw, Loader, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const ASSIGNMENT_TYPES = [
  'Baseline Report',
  'Employment Verification Survey',
  'Training Needs Assessment',
  'Snap Checking Survey',
];

const DD_ROLES = ['Deputy Director Public', 'Deputy Director Private', 'Deputy Director Procurement'];

const STATUS_STYLE = {
  'Pending Director Review': { bg: 'bg-amber-50', text: 'text-amber-700' },
  'Sent Back by Director': { bg: 'bg-orange-50', text: 'text-orange-700' },
  'Director Rejected': { bg: 'bg-red-50', text: 'text-red-700' },
  'Pending Secretary Approval': { bg: 'bg-blue-50', text: 'text-blue-700' },
  'Sent Back by Secretary': { bg: 'bg-orange-50', text: 'text-orange-700' },
  'Secretary Rejected': { bg: 'bg-red-50', text: 'text-red-700' },
  'Approved': { bg: 'bg-green-50', text: 'text-green-700' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || { bg: 'bg-gray-50', text: 'text-gray-700' };
  return <span className={`text-xs font-medium px-2 py-1 rounded-full ${s.bg} ${s.text}`}>{status}</span>;
};

const AssignmentWorkflow = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [institutions, setInstitutions] = useState([]);
  const [officers, setOfficers] = useState([]);

  const isDD = DD_ROLES.includes(user?.role);
  const isDirector = user?.role === 'Director' || user?.role === 'Super Admin';
  const isSecretary = user?.role === 'Secretary' || user?.role === 'Super Admin';

  // New request form (DD)
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ assignment_type: ASSIGNMENT_TYPES[0], institution_id: '', reason: '' });
  const [submittingNew, setSubmittingNew] = useState(false);

  // Decision modal
  const [decisionTarget, setDecisionTarget] = useState(null); // { request, stage: 'director'|'secretary' }
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionOfficer, setDecisionOfficer] = useState('');
  const [decisionDueDate, setDecisionDueDate] = useState('');
  const [decisionPriority, setDecisionPriority] = useState('Normal');
  const [submittingDecision, setSubmittingDecision] = useState(false);

  // Resubmit form
  const [resubmitTarget, setResubmitTarget] = useState(null);
  const [resubmitReason, setResubmitReason] = useState('');

  useEffect(() => {
    fetchRequests();
    if (isDD) fetchInstitutions();
    if (isSecretary) fetchOfficers();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/assignment-requests');
      setRequests(res.data.requests || []);
    } catch (err) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const res = await api.get('/institutions');
      setInstitutions(res.data.institutions || []);
    } catch {}
  };

  const fetchOfficers = async () => {
    try {
      const res = await api.get('/users?role=Monitoring Officer');
      setOfficers(res.data.users || []);
    } catch {}
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!newForm.reason.trim()) return toast.error('Please explain the reason for this request');
    setSubmittingNew(true);
    try {
      await api.post('/assignment-requests', newForm);
      toast.success('Request raised — pending Director review');
      setShowNewForm(false);
      setNewForm({ assignment_type: ASSIGNMENT_TYPES[0], institution_id: '', reason: '' });
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to raise request');
    } finally {
      setSubmittingNew(false);
    }
  };

  const openDecision = (request, stage) => {
    setDecisionTarget({ request, stage });
    setDecisionComment('');
    setDecisionOfficer('');
    setDecisionDueDate('');
    setDecisionPriority('Normal');
  };

  const submitDecision = async (decision) => {
    if (decision === 'Approved' && decisionTarget.stage === 'secretary' && !decisionOfficer) {
      return toast.error('Please select a Field Officer to approve');
    }
    setSubmittingDecision(true);
    try {
      const endpoint = decisionTarget.stage === 'director' ? 'director-decision' : 'secretary-decision';
      const payload = { decision, comment: decisionComment };
      if (decisionTarget.stage === 'secretary') {
        Object.assign(payload, {
          assigned_to: decisionOfficer || undefined,
          due_date: decisionDueDate || undefined,
          priority: decisionPriority,
        });
      }
      await api.put(`/assignment-requests/${decisionTarget.request.id}/${endpoint}`, payload);
      toast.success(`Request ${decision.toLowerCase()}`);
      setDecisionTarget(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record decision');
    } finally {
      setSubmittingDecision(false);
    }
  };

  const submitResubmit = async () => {
    try {
      await api.put(`/assignment-requests/${resubmitTarget.id}/resubmit`, { reason: resubmitReason });
      toast.success('Resubmitted for Director review');
      setResubmitTarget(null);
      setResubmitReason('');
      fetchRequests();
    } catch (err) {
      toast.error('Failed to resubmit');
    }
  };

  const pendingForMe = requests.filter(r =>
    (isDirector && r.status === 'Pending Director Review') ||
    (isSecretary && r.status === 'Pending Secretary Approval')
  );
  const myRequests = requests.filter(r => isDD);
  const otherRequests = requests.filter(r => !pendingForMe.includes(r));

  return (
    <Layout title="Special Assignment Requests">
      <div className="max-w-5xl mx-auto space-y-6">

        <div className="bg-blue-900 text-white rounded-xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList size={28} />
            <div>
              <h2 className="text-lg font-bold">Special Assignment Requests</h2>
              <p className="text-blue-300 text-xs mt-0.5">
                Deputy Director raises → Director reviews → Secretary approves → auto-assigned to Field Officer
              </p>
            </div>
          </div>
          {isDD && (
            <button
              onClick={() => setShowNewForm(!showNewForm)}
              className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-semibold px-4 py-2 rounded-lg text-sm transition"
            >
              <Send size={16} /> Raise Request
            </button>
          )}
        </div>

        {showNewForm && isDD && (
          <div className="bg-white rounded-xl shadow border border-blue-100 p-6">
            <h3 className="font-semibold text-blue-900 mb-4">New Assignment Request</h3>
            <form onSubmit={handleCreateRequest} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Type</label>
                  <select
                    value={newForm.assignment_type}
                    onChange={e => setNewForm({ ...newForm, assignment_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ASSIGNMENT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                  <select
                    value={newForm.institution_id}
                    onChange={e => setNewForm({ ...newForm, institution_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select institute...</option>
                    {institutions.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Justification *</label>
                <textarea
                  value={newForm.reason}
                  onChange={e => setNewForm({ ...newForm, reason: e.target.value })}
                  rows={3}
                  placeholder="Explain why this assignment is needed..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submittingNew}
                  className="flex items-center gap-2 bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 text-white px-5 py-2 rounded-lg text-sm font-medium transition"
                >
                  {submittingNew ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                  Submit for Director Review
                </button>
                <button type="button" onClick={() => setShowNewForm(false)} className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-400 flex items-center justify-center gap-2">
            <Loader size={16} className="animate-spin" /> Loading...
          </div>
        ) : (
          <>
            {pendingForMe.length > 0 && (
              <div className="bg-white rounded-xl shadow border border-amber-200">
                <div className="p-4 border-b border-gray-100 bg-amber-50 rounded-t-xl">
                  <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                    <AlertCircle size={16} /> Awaiting Your Decision ({pendingForMe.length})
                  </h3>
                </div>
                <div className="divide-y divide-gray-50">
                  {pendingForMe.map(r => (
                    <div key={r.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{r.assignment_type}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{r.institution_name || 'No institute specified'} · Requested by {r.requested_by_name}</p>
                          <p className="text-sm text-gray-600 mt-2">{r.reason}</p>
                        </div>
                        <StatusBadge status={r.status} />
                      </div>
                      <button
                        onClick={() => openDecision(r, isDirector && r.status === 'Pending Director Review' ? 'director' : 'secretary')}
                        className="mt-3 bg-blue-800 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-medium transition"
                      >
                        Review & Decide
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow border border-gray-100">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800">
                  {isDD ? 'My Requests' : 'All Requests'}
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{requests.length}</span>
                </h3>
              </div>
              {requests.length === 0 ? (
                <div className="p-12 text-center">
                  <ClipboardList size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm">No assignment requests yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {requests.map(r => (
                    <div key={r.id} className="p-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-800 text-sm">{r.assignment_type}</p>
                          <StatusBadge status={r.status} />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {r.institution_name || '—'} · by {r.requested_by_name}
                          {r.assigned_to_name ? ` · assigned to ${r.assigned_to_name}` : ''}
                        </p>
                        {r.director_comment && <p className="text-xs text-gray-400 mt-1">Director: {r.director_comment}</p>}
                        {r.secretary_comment && <p className="text-xs text-gray-400 mt-1">Secretary: {r.secretary_comment}</p>}
                      </div>
                      {isDD && ['Sent Back by Director', 'Sent Back by Secretary'].includes(r.status) && (
                        <button
                          onClick={() => { setResubmitTarget(r); setResubmitReason(r.reason); }}
                          className="flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg font-medium"
                        >
                          <RotateCcw size={12} /> Resubmit
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Decision Modal */}
        {decisionTarget && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h3 className="font-semibold text-gray-800 mb-1">{decisionTarget.request.assignment_type}</h3>
              <p className="text-xs text-gray-500 mb-4">{decisionTarget.request.institution_name || '—'}</p>

              {decisionTarget.stage === 'secretary' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Field Officer *</label>
                  <select
                    value={decisionOfficer}
                    onChange={e => setDecisionOfficer(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
                  >
                    <option value="">Select officer...</option>
                    {officers.map(o => <option key={o.id} value={o.id}>{o.full_name}</option>)}
                  </select>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={decisionDueDate}
                        onChange={e => setDecisionDueDate(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        value={decisionPriority}
                        onChange={e => setDecisionPriority(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option>Normal</option>
                        <option>Urgent</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
              <textarea
                value={decisionComment}
                onChange={e => setDecisionComment(e.target.value)}
                rows={3}
                placeholder="Optional comment..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => submitDecision('Approved')}
                  disabled={submittingDecision}
                  className="flex-1 flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-xs font-medium"
                >
                  <CheckCircle size={14} /> Approve
                </button>
                <button
                  onClick={() => submitDecision('Sent Back')}
                  disabled={submittingDecision}
                  className="flex-1 flex items-center justify-center gap-1 bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-2 rounded-lg text-xs font-medium"
                >
                  <RotateCcw size={14} /> Send Back
                </button>
                <button
                  onClick={() => submitDecision('Rejected')}
                  disabled={submittingDecision}
                  className="flex-1 flex items-center justify-center gap-1 bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-lg text-xs font-medium"
                >
                  <XCircle size={14} /> Reject
                </button>
              </div>
              <button onClick={() => setDecisionTarget(null)} className="w-full mt-3 text-xs text-gray-400 hover:text-gray-600">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Resubmit Modal */}
        {resubmitTarget && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Resubmit Request</h3>
              <label className="block text-sm font-medium text-gray-700 mb-1">Updated Reason</label>
              <textarea
                value={resubmitReason}
                onChange={e => setResubmitReason(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
              />
              <div className="flex gap-2">
                <button onClick={submitResubmit} className="flex-1 bg-blue-800 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium">
                  Resubmit for Director Review
                </button>
                <button onClick={() => setResubmitTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default AssignmentWorkflow;
